import { fetchAuthSession } from 'aws-amplify/auth';
import { DivConst } from 'constants/divConst';

import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import mime from 'mime';
import { useAtomValue } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { uploadS3 } from 'utils/app';

// SignupOldCompany.tsx の FormStateType と同じ shape を表す型。
// optional は使わず string で受け取り、hook 内で空文字を null へ正規化する
// (postCompanyRegister の API 型はすべて `string | null` 必須のため)。
type RegisterFormData = {
  companyName: string;
  companyTel: string;
  zipCode: string;
  prefectureDiv: number;
  city: string;
  address: string;
  buildingName: string;
  reportMailSenderName: string;
  reportMailReplyAddress: string;
  reportMailSignatureHtml: string;
  privacyPolicyUrl: string;
  copyright: string;
  apiKeyBenchmark: string;
  apiKeyGoogleAnalytics: string;
  userName: string;
};

export function useCompanyRegistrationOld(): {
  registerCompany: (
    data: RegisterFormData,
    imageBlob: Blob | null
  ) => Promise<{ duplicated: boolean }>;
} {
  const { postCompanyRegister } = useApiState();
  const { initializeAcctState } = useAppState();
  const acctState = useAtomValue(AcctState);

  const registerCompany = async (
    data: RegisterFormData,
    imageBlob: Blob | null
  ): Promise<{ duplicated: boolean }> => {
    const session = await fetchAuthSession();

    // useCompanyRegistration と同じ早期 throw パターン。
    // acctState.user が null の場合のみ userPayload を構築する。
    let userPayload: { accessToken: string; name: string; loginRouteDiv: number } | null = null;
    if (!acctState.user) {
      // fetchAuthSession の戻り値型では tokens / accessToken が optional。
      const accessToken = session.tokens?.accessToken?.toString();
      if (!accessToken) {
        throw new Error('Cognito access token is not available.');
      }
      userPayload = {
        accessToken,
        name: data.userName,
        loginRouteDiv: DivConst.items.loginRoute.mail.value,
      };
    }

    const imageUploadKey = imageBlob
      ? await uploadS3(imageBlob, mime.getExtension(imageBlob.type))
      : null;
    const response = await postCompanyRegister({
      company: {
        name: data.companyName,
        tel: data.companyTel,
        zipCode: data.zipCode,
        prefectureDiv: data.prefectureDiv,
        city: data.city,
        address: data.address,
        // 空文字を null に正規化（API 型は `string | null`）
        buildingName: data.buildingName || null,
        imageUploadKey,
        reportMailSenderName: data.reportMailSenderName,
        reportMailReplyAddress: data.reportMailReplyAddress,
        reportMailSignatureHtml: data.reportMailSignatureHtml,
        privacyPolicyUrl: data.privacyPolicyUrl,
        copyright: data.copyright || null,
        termsInputDiv: DivConst.items.termsInputType.url.value,
        termsUrl: null,
        termsText: null,
        apiKeyBenchmark: data.apiKeyBenchmark || null,
        apiKeyGoogleAnalytics: data.apiKeyGoogleAnalytics || null,
      },
      user: userPayload,
    });
    if (response.error?.duplicated) {
      return { duplicated: true };
    }
    await initializeAcctState(response.companyCd);
    return { duplicated: false };
  };

  return { registerCompany };
}
